import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { ExportsService } from '../services/exports.service';
import { LoginService } from '../services/login.service';
import { AppConstants } from '../shared/app.constants';
import { AppUtils } from '../shared/app.utils';
import { DateRangeFilterModel } from '../shared/models/date-range-filter-model';
import { ExportsData, ExportTypeItem } from '../shared/models/exports-data-model';
import { ExportType } from '../shared/types/export.types';
import { AuthorizationModel } from '../shared/models/authorization-model';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'ness-exports',
  templateUrl: './exports.component.html',
  styles: [".d-flex[hidden] { display: none !important; }"]
})
export class ExportsComponent implements OnInit, OnDestroy {
  readonly storageKey: string = "exportsFilterModel";
  readonly canView: AuthorizationModel = {
    role: [],
    authOrgCode: null,
    userType: [],
    roleUserType: [
      { roles: ['Application Administrator', 'Power User', 'NESS Administrator'], userType: 'ATFHQ' },
      { roles: ['NESS Administrator'], userType: 'ATFFieldDivision' },
      { roles: ['NESS Administrator'], userType: 'StateLocalLE' },
    ]
  } as AuthorizationModel;

  readonly canViewDropdown: AuthorizationModel = {
    role: [],
    authOrgCode: null,
    userType: [],
    roleUserType: [
      { roles: ['Application Administrator', 'Power User', 'NESS Administrator'], userType: 'ATFHQ' },
      { roles: ['NESS Administrator', 'Power User', 'Application Administrator'], userType: 'ATFFieldDivision' },
    ]
  } as AuthorizationModel;
  dateRangeDuration: number = AppConstants.exportsDateRangeDurationDays;
  dateRangeVerbiage: string = 'Filter by date range';
  exportTypes: ExportTypeItem[] = [];
  exportTypeModel: ExportsData = {
    exportTypeId: '',
    filterValues: AppUtils.createDateRangeFilterModel(this.storageKey, this.dateRangeDuration)
  } as ExportsData;
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.loginService.fieldDivisions$;
  exports: ServerModels.ExportViewModel[];
  hideDateRangeEl: boolean = false;
  showConfirmExport: boolean = false;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(public messageService: MessageService,
    private exportsService: ExportsService,
    private loginService: LoginService) { }

  ngOnInit(): void {
    this.exportsService.getAllExportTypes().pipe(takeUntil(this._unsubscribe$)).subscribe((exportType: ServerModels.ENESS_REF_ExportTypes[]) => {
      this.exportTypes = exportType.map(x => {
        return {
          type: <ExportType>x.ExportTypeName,
          typeId: x.ExportTypeID,
          description: x.ExportTypeDescription,
          dateRangeDuration: AppConstants.exportsDateRangeDurationDays,
          dateRangeFilter: this._getDateRangeFilter(x),
          dateRangeVerbiage: this._getDateRangeVerbiage(x),
          fieldDivisionFilter: true
        } as ExportTypeItem;
      });
      this.exportTypes = this.exportTypes.filter(x => x.type !== 'Crime Gun Step Out' && x.type !== 'RMS Events');
    });
    this._init();
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onRefreshExports(): void {
    this._loadExportsData();
  }

  public onSelectExportType(evt: any): void {
    if (!!evt.currentTarget.value) {
      let exportType: ExportTypeItem = this.exportTypes.find(x => x.typeId == evt.currentTarget.value);
      this.hideDateRangeEl = !exportType.dateRangeFilter;
      if (exportType.dateRangeFilter) {
        this.dateRangeVerbiage = exportType.dateRangeVerbiage;
        this.dateRangeDuration = exportType.dateRangeDuration;
      }
      if (exportType.fieldDivisionFilter) {
        this.exportTypeModel.filterValues.cgicCode = 'all';
      }
    } else {
      this.hideDateRangeEl = false;
      this.dateRangeDuration = AppConstants.exportsDateRangeDurationDays;
      this.dateRangeVerbiage = 'Filter by date range';
    }
  }

  public onSubmitExportTypes(): void {
    this.showConfirmExport = true;
  }

  public resetExportForm(): void {
    this.hideDateRangeEl = false;
    this.exportTypeModel.exportTypeId = '';
    this.exportTypeModel.filterValues = new DateRangeFilterModel(AppUtils.userFieldDivisionID(), this.dateRangeDuration);
  }

  onCloseConfirmation(): void {
    this.showConfirmExport = false;
  }

  onConfirmSubmit(): void {
    let exportStartedRow = {
      ExportTypeID: this.exportTypeModel.exportTypeId,
      ExportTypeName: this.exportTypes.find(x => x.typeId == this.exportTypeModel.exportTypeId).type,
      Status: 'Started'
    } as ServerModels.ExportViewModel;

    this.exportsService.generateExportData(this.exportTypeModel).pipe(take(1)).subscribe(() => {
      this.resetExportForm();
      this.exports = [exportStartedRow, ...this.exports];
      this.showConfirmExport = false;
    });
  }

  public yearRange(): string {
    let currentYear = new Date().getFullYear().toString();
    return `${AppConstants.defaultFilterYearRangeStart}:${currentYear}`;
  }

  private _getDateRangeFilter(x: ServerModels.ENESS_REF_ExportTypes): boolean {
    switch (<ExportType>x.ExportTypeName) {
      case 'NIBIN Ballistic Evidence':
        return true;
      case 'Crime Gun Overview':
        return true;
      case 'RMS Events':
        return true;
      case 'RMS People':
        return true;
      case 'RMS Locations':
        return true;
      default:
        return false;
    }
  }

  private _getDateRangeVerbiage(x: ServerModels.ENESS_REF_ExportTypes): string {
    switch (<ExportType>x.ExportTypeName) {
      case 'NIBIN Ballistic Evidence':
        return 'Filter by acquisition date range';
      case 'Crime Gun Overview':
        return 'Filter by lead date range';
      case 'RMS Events':
        return 'Filter by event date range';
      case 'RMS People':
        return 'Filter by event date range';
      case 'RMS Locations':
        return 'Filter by event date range';
      default:
        return 'Filter by date range';
    }
  }

  private _init(): void {
    this._loadExportsData();
    if (this.storageKey) {
      let fm: any;
      if (fm = JSON.parse(sessionStorage.getItem(this.storageKey))) {
        this.exportTypeModel.filterValues.startDate = new Date(fm._startDate);
        this.exportTypeModel.filterValues.endDate = new Date(fm._endDate);
      }
    }
    if (!this.exportTypeModel.filterValues.startDate)
      this.exportTypeModel.filterValues.startDate = new Date();
    if (!this.exportTypeModel.filterValues.endDate) {
      this.exportTypeModel.filterValues.endDate = new Date();
    }
    this.exportTypeModel.filterValues.endDate.setHours(23, 59, 59, 999);
    sessionStorage.setItem(this.storageKey, JSON.stringify(this.exportTypeModel.filterValues));
  }

  private _loadExportsData(): void {
    this.exportsService.getExportDataByUser().pipe(take(1)).subscribe(
      (exports: ServerModels.ExportViewModel[]) => this.exports = this.exportDisplayFilter(exports),
      (err:any) => this.messageService.add({ key: 'exportMsgs', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message })
    );
  }

  public exportDisplayFilter(exportsBeingChecked: ServerModels.ExportViewModel[]): ServerModels.ExportViewModel[] {
    exportsBeingChecked.forEach(x => {
      if (x.Status === 'In Progress' && x.Message != null && (x.ExpiresOn != null || x.GeneratedOn != null) ) {
        x.Status = 'Contact NESS Support';
      }
    });
    return exportsBeingChecked;
  }
}
